import { Component, ElementRef, Inject, OnInit, Renderer2, signal, TemplateRef, ViewChild, } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { ADPConfigService, ADPResourceService, EncriptionService } from 'digital-core';
import { AccountService } from 'src/app/core/services/account.service';
import { GoogleAnalyticsService } from 'src/app/core/services/google-tag-manager.service';
import { LoginService } from 'src/app/core/services/login.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { GTMLoginEvents } from 'src/app/model/google-tag-manager/api-status';
import { INotFoundModal } from 'src/app/model/sitefinity/LoginPage';
import { PreInitSettingsService } from '../../../../core/services/settings/pre-init-setting.service';
import { dashboardRouteWithSlash, myAccountRouteWithSlash, underMaintenanceRoute } from '../../../../route-constants';
import { FailedLoginModalComponent } from '../failed-login-modal/failed-login-modal.component';
import { StorageService } from 'src/app/core/services/storage.service';
import { PGHAPIService } from 'src/app/core/services/pgh-api-service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import OktaSignIn from '@okta/okta-signin-widget';
import { format } from 'date-fns';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  signIn: any;
  resetFlow = false;
  resetSuccessFlow = false;
  loginFlow = true;
  registerFlow = false;
  hasHeaderSubText= false;
  oktTokenStorage: string;
  xfinityAssurantSoldLink: string;
  @ViewChild('failedLoginModal') failedLoginModal: TemplateRef<any>;
  headerAsLogoImageCaption: string;
  private configSettings;
  private oktaPrefix: any;
  private checkInterval: any;
  redirectToMaintenancePage: boolean;
  createAccountRoute = signal('');

  constructor(
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private preInitAppSettings: PreInitSettingsService,
    private loginService: LoginService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private matDialog: MatDialog,
    private router: Router,
    private utilityService: UtilityService,
    private resourceService: ADPResourceService,
    private renderer: Renderer2,
    private elRef: ElementRef,
    private storageService: StorageService,
    private accountService: AccountService,
    public pghApiService: PGHAPIService,
    private configService: ADPConfigService,
    private httpClient: HttpClient
  ) {
    this.configSettings = this.preInitAppSettings.getSettings();
    this.hasHeaderSubText = this.resourceService.getPortaLabelValue("Login_Header_Sub_Text")!=null;
  }

  async ngOnInit() {
    if (this.utilityService.isNativeApp && document.querySelector<HTMLElement>(".login-container.container-fluid")) {
      document.querySelector<HTMLElement>(".login-container.container-fluid").style.display = 'none';
    }
    this.redirectToMaintenancePage = this.resourceService.isPortalSettingTrue("Global_Is_Portal_Under_Maintenance");
    if (this.redirectToMaintenancePage) {
      this.router.navigate([underMaintenanceRoute]);
    }
    this.headerAsLogoImageCaption = this.resourceService.getPortaLabelValue("Login_Display_Header_As_Logo_Title_Label");
    this.loginService.selectedContractId = undefined;
    this.xfinityAssurantSoldLink = this.resourceService.getPortalSetting('URL_Xfinity_Assurant_Sold').Value;

    const oktaSessionExists = false;
    const selectedSiteSetting = this.preInitAppSettings.getSiteSettings();
    const oktTokenStorage = localStorage.getItem('okta-token-storage');
    if (oktaSessionExists || oktTokenStorage) {
      const tokenDetail = oktaSessionExists ? await this.getToken()
        : (oktTokenStorage ? { isValid: this.isValidateToken(oktTokenStorage), token: JSON.parse(oktTokenStorage) }
          : { isValid: false, token: null });

      if (tokenDetail.isValid) {
        const tokens = tokenDetail.token;
        const idToken = oktaSessionExists ? tokens.tokens.idToken
          : (tokens.id_token ? tokens.id_token : tokens.idToken);

        const accessToken = oktaSessionExists ? tokens.tokens.accessToken
          : (tokens.access_token ? tokens.access_token : tokens.accessToken);

        const token = oktaSessionExists ? tokens.tokens.accessToken.accessToken
          : (tokens.access_token && tokens.access_token.accessToken ? tokens.access_token.accessToken
            : tokens.accessToken.accessToken);

        const decodedToken = this.oktaAuth.token.decode(token);
        let isExists = false;
        // Check exist in group or not
        if (decodedToken && decodedToken.payload && Array.isArray(decodedToken.payload.groups)) {
          // Check if groups array has length and perform further operations
          if (decodedToken.payload.groups.length > 0) {
            isExists = decodedToken.payload.groups.some(x =>
              x.toString().toLowerCase().trim() === this.preInitAppSettings.getSiteSettings().groupName.toLowerCase().trim()
            );
          }
        }

        let isTokenExpired = false;
        if (decodedToken?.payload?.exp) {
          const expiryDate = new Date(decodedToken?.payload?.exp * 1000);
          const now = new Date();
          if (now > expiryDate) {
            isTokenExpired = true;
          }
        }

        if (isExists && !isTokenExpired) {
          this.oktaAuth.tokenManager.add('id_token', idToken);
          this.oktaAuth.tokenManager.add('access_token', accessToken);
          const isNative = this.utilityService.isNativeApp;
          if (isNative) {
            let defaultPage = myAccountRouteWithSlash;
            const lastPage = sessionStorage.getItem('last-page-access');

            if (lastPage !== null && lastPage.toLowerCase().indexOf('claims') > -1) {
              defaultPage = 'claims';
              sessionStorage.removeItem('last-page-access');
            }

            if (lastPage !== null && lastPage.toLowerCase().indexOf('tech-services') > -1) {
              defaultPage = 'tech-services';
              sessionStorage.removeItem('last-page-access');
            }
            this.router.navigate([defaultPage]).then(
              () => { window.location.reload(); });
          } else {
            if (window.location.href.toLowerCase().endsWith('/login?redirect=claims')) {
              sessionStorage.setItem('redirect-to', 'gssp-claims');
              this.router.navigate([dashboardRouteWithSlash]);
            } else {
              this.router.navigate([dashboardRouteWithSlash]).then(() => {
                window.location.reload();
              });
            }
          }
        }
      }
    }
    let sessionPrefetch = this.resourceService.isPortalSettingTrue('Global_Session_Initiate_OnPageLoad');
    if(sessionPrefetch) {
      let sessionId = this.storageService.getLocal('SessionId');
      if(sessionId)
        this.initSession(sessionId.toString());
    }
    // TODO: set this to the current culture that Sitefinity is set to for the app and derive it to a culture that Okta understands (maybe grab the first two characters of the sitefinity culture should work or build a manual map from SF culture to Okta culture).
    //       There are sooo many properties that Okta has that we have to set the language in Okta for users to understand the multitude of error messages that Okta has
    const currentOktaCultureString = 'en';
    const exceedAttempt = this.resourceService.getViewLabel('Login_ExceededMaxAttempts');
    const attemptRemaining = this.resourceService.getViewLabel('Login_AttemptRemaining');
    const showLoginAttemptCounter = this.resourceService.isPortalSettingTrue('Global_ShowLoginAttempt_Counter');
    const lockoutMessage = this.resourceService.getViewLabel('Login_LockedoutMessage');

    // Overrides default text that appears in the Okta widget.
    // Here is where you can see the available properties: https://github.com/okta/okta-signin-widget/blob/master/packages/%40okta/i18n/src/properties/login.properties
    const oktaContentOverrides = {
      'primaryauth.title': ' ',
      'primaryauth.submit': this.resourceService.getViewLabel('Login_SignIn_Button_Text'),
      'primaryauth.username.placeholder': this.resourceService.getViewLabel('Login_SignInForm_Email_Label'),
      'primaryauth.password.placeholder': this.resourceService.getViewLabel('Login_SignInForm_Password_Label'),
      forgotpassword: this.resourceService.getViewLabel('Login_ForgotPassword_Link_Text'),
      remember: this.resourceService.getViewLabel('Login_SignInForm_RememberMe_Label'),
      'password.forgot.emailSent.title': this.resourceService.getViewLabel('Login_PasswordReset_EmailSent_Header_Text'),
      'password.forgot.emailSent.desc': this.resourceService.getViewLabel('Login_PasswordReset_EmailSent_Body_Text'),
      'errors.E0000004': this.resourceService.getViewLabel('Login_SignIn_Error_E0000004_Text'),
      'errors.E0000034': this.resourceService.getViewLabel('Login_Error_E0000034_Text'),
      'error.username.required': this.resourceService.getViewLabel('Login_SignIn_Error_UsernameRequired_Text'),
      'error.password.required': this.resourceService.getViewLabel('Login_SignIn_Error_PasswordRequired_Text'),
      'password.reset.title.generic': this.resourceService.getViewLabel('Login_PasswordReset_Header_Text'),
      'password.reset': this.resourceService.getViewLabel('Login_PasswordReset_Header_Text'),
      'password.forgot.email.or.username.placeholder': this.resourceService.getViewLabel('Login_PasswordResetForm_Email_Label'),
      'password.forgot.email.or.username.tooltip': this.resourceService.getViewLabel('Login_PasswordResetForm_Email_Label'),
      'password.forgot.sendEmail': this.resourceService.getViewLabel('Login_PasswordReset_Button_Text'),
      goback: this.resourceService.getViewLabel('Login_PasswordReset_GoBack_Text'),
      backToSignin: this.resourceService.getViewLabel('Login_PasswordReset_GoBack_Text'),
    };

    let usernameAttemptCounts: { [username: string]: { attempts: number, timestamp: number } } = {};
    const maxAttempts = 5;
    this.signIn = new OktaSignIn({
      baseUrl: this.configSettings.okta.baseUrl,
      clientId: this.configSettings.okta.clientId,
      redirectUri: selectedSiteSetting.redirectUri,
      language: currentOktaCultureString,
      useClassicEngine: true,
      authParams: {
        //pkce: true,
        //responseMode: 'query',
        //display: 'page',
        scopes: this.configSettings.okta.scopes,
        issuer: this.configSettings.okta.issuer,
        responseType: ['id_token', 'token']
      },
      features: {
        recovery: true,
        selfServiceUnlock: false,
        passwordExpired: true,
        registration: false,
        showPasswordToggleOnSignInPage: true, // Add this
        showPasswordToggleOnRecoveryPage: true // Add this for recovery pages
      },
      i18n: {
        // Overrides default text that appears in the Okta widget.
        // Here is where you can see the available properties: https://github.com/okta/okta-signin-widget/blob/master/packages/%40okta/i18n/src/properties/login.properties
        [currentOktaCultureString]: oktaContentOverrides,
      },
      transformUsername: (username: string) => {
        if (!username.startsWith(appCode.toUpperCase())) {
          username = selectedSiteSetting.oktaPrefix.toUpperCase() + username;
        }
        try {
          window['OktaErrorMessage'] = '';
          window['lastEmail'] = username;
          usernameAttemptCounts = this.loadUsernameAttemptCountsFromLocalStorage();
          // Check if the attempt count is expired
          const existingAttempt = usernameAttemptCounts[username];
          if (existingAttempt && this.isAttemptCountExpired(existingAttempt)) {
            delete usernameAttemptCounts[username];
            existingAttempt.attempts = 0;
          }

          // Update or create the attempt count
          usernameAttemptCounts[username] = {
            attempts: (existingAttempt?.attempts || 0) + 1,
            timestamp: Date.now()
          };
          const attemptsLeft = maxAttempts - usernameAttemptCounts[username].attempts;
          if (attemptsLeft <= 0) {
            window['OktaErrorMessage'] = exceedAttempt;
          } else {
            window['OktaErrorMessage'] = attemptRemaining.replace('{0}', attemptsLeft.toString());
          }
          // console.log('Current State : ' + username  + " --- " + window['OktaErrorMessage']);
          this.saveUsernameAttemptCountsToLocalStorage(usernameAttemptCounts);
        } catch (e) { }
        return username;
      }
    });

    // Handle recovery token
    const urlParams = new URLSearchParams(window.location.search);
    const recoveryToken = urlParams.get('recovery_token');

    if (recoveryToken) {
      this.signIn.renderEl(
        {
          el: '#sign-in-widget',
          recoveryToken: recoveryToken
        },
        (res) => {
            this.oktaAuth.tokenManager.add('id_token', res.tokens.idToken);
            this.oktaAuth.tokenManager.add('access_token', res.tokens.accessToken);
            window.location.href = '/dashboard';
        },
        (err) => {
          console.error('Recovery error:', err);
        }
      );
    } else {
      this.signIn.renderEl({ el: '#sign-in-widget' }, (res) => {
        if (res.status === 'SUCCESS') {
          this.oktaAuth.tokenManager.add('id_token', res.tokens.idToken);
          this.oktaAuth.tokenManager.add('access_token', res.tokens.accessToken);
          window.location.href = '/dashboard';
          localStorage.setItem('gtm_login_success', 'true');
        }
      });
    }
    // This is to get around a bug with "Remember Me" functionality
    // When you return to login it transforms username to PG__{username}
    // Setting the value isn't good enough, we have to simulate user entering it
    const appCode = selectedSiteSetting.oktaPrefix;
    if (this.signIn) {
      this.signIn.on('ready', () => {
        this.oktaPrefix = appCode;
        this.findForgotPasswordButton();
        // The Widget is ready for user input
        const elem = document.querySelector('#okta-signin-username');
        const usernameInput = elem as HTMLInputElement;
        this.replaceUsername(usernameInput);
        setTimeout(() => {
           this.attachUsernameEvent();
        }, 1000); // Adjust the delay as needed

        const signInButton = document.querySelector('#okta-signin-submit');

        signInButton?.addEventListener('click', e => {
          if (window.location.href.toLowerCase().endsWith('/login?redirect=claims')) {
            sessionStorage.setItem('redirect-to', 'gssp-claims');
          } else {
            this.storageService.clearLocal();
          }
          this.googleAnalyticsService.emitLoginEvent(GTMLoginEvents.Login);
        });

        const mCallback = (mutations: any) => {
          for (const mutation of mutations) {
            const mutationText: string = mutation.target.textContent;
            const errorText = 'Incorrect email or password';
            if (mutationText.includes(errorText)) {
              if (showLoginAttemptCounter) {
                const errorDiv = document.querySelector('.infobox-error p');
                errorDiv.classList.add('invisible');
                this.accountService.isUserLockedOut(window['lastEmail']).subscribe(timeRemaining => {
                  if (timeRemaining <= 0) { // do nothing
                    if (errorDiv) {
                      errorDiv.innerHTML = window['OktaErrorMessage'];
                      errorDiv.classList.remove('invisible');
                    }
                  }
                  if (timeRemaining != 0) {
                    if (errorDiv) {
                      errorDiv.innerHTML = lockoutMessage.replace('@minutes', timeRemaining.toString());
                      errorDiv.classList.remove('invisible');
                    }
                  }
                });
              }
              const emitError = {
                message: errorText,
                code: '401',
                type: 'Unauthorized'
              }
              this.googleAnalyticsService.emitLoginEvent(GTMLoginEvents.LoginError, emitError);
            }
          }
        };

        const mList = document.querySelector('.o-form-error-container');
        const options = {
          childList: true,
          subtree: true
        };
        const observer = new MutationObserver(mCallback);

        observer.observe(mList, options);
      });
    }

    if (this.loginService.failedLoginBycontract) {
      if (this.loginService.failedLoginByUserGroup) {
        this.loginService.failedLoginBycontract = false;
        this.InitiateAccountNotFoundError();
      } else {
        this.matDialog.closeAll();
        this.loginService.failedLoginBycontract = false;
        this.matDialog.open(FailedLoginModalComponent, {
          data: this.getFailedLoginModalContentData('ContractNotFound'),
        });
      }
    } else if (this.loginService.failedLoginByContractCancalled && this.resourceService.getPortalSetting('Login_Show_FailedLoginModal_For_ContractCancelled').Value.toLowerCase() === 'true') {
      this.matDialog.closeAll();
      this.loginService.failedLoginByContractCancalled = true;
      this.matDialog.open(FailedLoginModalComponent, {
        data: this.getFailedLoginModalContentData('ContractCancelled'),
      });
    }
    else {
      this.InitiateAccountNotFoundError();
    }

    this.createAccountRoute.set(this.resourceService.getPortalSetting('URL_Create_Account').Value);
  }

  private attachUsernameEvent(): void {

    let userSetting = this.resourceService.isPortalSettingTrue("Global_UXOptimization_Enabled");
    if(userSetting == false)
      return;
    const usernameInput = document.getElementById('okta-signin-username') as HTMLInputElement;
    if (usernameInput) {
      // Listen for the blur event in case the user manually changes the field.
      usernameInput.addEventListener('blur', (event: Event) => {
        const input = event.target as HTMLInputElement;
        const email = input.value;
        // console.log('Blur Email: ' + usernameInput.value);
        if (email) {
          this.initSession(email);
        }
      });
      usernameInput.focus();
      usernameInput.blur();
      usernameInput.focus();
    }
  }

  lastEmail = '';
  async initSession(email: string): Promise<void> {
    // track user session
    const selectedSiteSetting = this.preInitAppSettings.getSiteSettings();
    if (this.lastEmail != email) {
      const data = { SessionId: selectedSiteSetting.oktaPrefix.toUpperCase()  + email };
      this.httpClient.post<string>(this.configService.cmsApiUrl+'/initSession', JSON.stringify(data)).subscribe((data) => {
      });

      this.storageService.setLocal('SessionId', email, true);
      this.lastEmail = email;
    }
  }

  findForgotPasswordButton() {
    this.checkInterval = setInterval(() => {
      const forgotPassword = this.elRef.nativeElement.querySelector('.js-forgot-password');
      if (forgotPassword) {
        this.renderer.listen(forgotPassword, 'click', () => {
          this.googleAnalyticsService.emitClickEvent('click', this.resourceService.getViewLabel('Login_ForgotPassword_Link_Text'), '', null, null);
          this.findBackButton();
        });
        clearInterval(this.checkInterval);
      }
    }, 200);
  }

  findEmailButton() {
    this.checkInterval = setInterval(() => {
    const originalButton = this.elRef.nativeElement.querySelector('.email-button');

    if (originalButton) {
      clearInterval(this.checkInterval);

      // Clone original button to create a wrapper button
      const clonedButton = originalButton.cloneNode(true) as HTMLButtonElement;

      // Replace original with the clone in DOM
      originalButton.parentNode?.replaceChild(clonedButton, originalButton);

      // Now add listener to cloned button
      this.renderer.listen(clonedButton, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const emailInput = this.elRef.nativeElement.querySelector('input[name="username"]') as HTMLInputElement;
        const email = emailInput?.value;
        if (!email) {
          return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(email)) {
           throw new HttpErrorResponse({
                      error: {
                        ErrorId: `PGH-UI-${format(new Date(), 'yyyyMMdd-HHmmssSSS')}`,
                        Source: 'ResetPassword',
                        ErrorCode: 'InvalidEmail'
                      }
                    });
        }
        try {
          var body = { factorType: 'Email', username: this.oktaPrefix.toUpperCase() + email};
          var config = window['config'];

          var accountSetUserClientUrl =  config.core.cmsApiUrl + "/forgotpassword?appId="
          fetch(accountSetUserClientUrl, {
                    method: 'POST',
                    headers: {
                      'Ocp-Apim-Subscription-Key': config.subscriptionKey,
                      'ParentCMS':   config.core.parentCMS,
                      'ClientCMS':   config.core.clientCMS,
                      'ClientDomain': window.location.protocol + "//" + window.location.hostname,
                      'Accept': 'application/json',
                      'Content-Type': 'application/json'
                      },
                      // We'll assume `req.body` is already a JSON string.
                      body: JSON.stringify(body)
                  }
                  ).then(response => {
                        if (!response.ok) {
                          // Return a custom error to the callback
                          return response.text().then(errorText => {
                            throw new Error(`HTTP ${response.status} – ${errorText}`);
                        });
                    }
                    return '';
                    }).then(() => {
                       setTimeout(() => {
                        this.googleAnalyticsService.emitClickEvent('click', this.resourceService.getViewLabel('Login_PasswordReset_Button_Text'), '', null, null);

                        originalButton.click();
                    }, 1000);
          });
        } catch (err: any) {

        }
      });
    }
  }, 200);
}

  findEmailButton1() {
    this.checkInterval = setInterval(() => {
      const forgotPassword = this.elRef.nativeElement.querySelector('.email-button');
      if (forgotPassword) {
        this.renderer.listen(forgotPassword, 'click', () => {

          console.log('click event triggered');
          this.googleAnalyticsService.emitClickEvent('click', this.resourceService.getViewLabel('Login_PasswordReset_Button_Text'), '', null, null);
          //this.findReturnToSigninButton();
        });
        clearInterval(this.checkInterval);
      }
    }, 200);
  }

  findReturnToSigninButton() {
    this.checkInterval = setInterval(() => {
      const returnToSigninButton = this.elRef.nativeElement.getElementsByClassName('link-button')[0];
      if (returnToSigninButton.getAttribute('data-se') === 'back-button') {
        this.renderer.listen(returnToSigninButton, 'click', () => {
          this.findUsername();
        });
        clearInterval(this.checkInterval);
      }
    }, 200);
  }

  findBackButton() {
    this.checkInterval = setInterval(() => {
      const backButton = this.elRef.nativeElement.querySelector('.js-back');
      if (backButton) {
        this.attachEventToElement(backButton);
        clearInterval(this.checkInterval);
        this.findEmailButton();
      }
    }, 200);
  }

  findUsername() {
    this.checkInterval = setInterval(() => {
      const usernameInput = this.elRef.nativeElement.querySelector('#okta-signin-username');
      if (usernameInput) {
        this.replaceUsername(usernameInput);
        clearInterval(this.checkInterval);
        this.findForgotPasswordButton();
      }
    }, 200);
  }

  replaceUsername(usernameInput: any) {
    if (usernameInput && usernameInput.value.startsWith(this.oktaPrefix.toUpperCase())) {
      usernameInput.value = usernameInput.value.replace(this.oktaPrefix.toUpperCase(), '');
    }
  }

  attachEventToElement(element: HTMLElement): void {
    this.renderer.listen(element, 'click', () => {
      this.googleAnalyticsService.emitClickEvent('click', this.resourceService.getViewLabel('Login_PasswordReset_GoBack_Text'), '', null, null);
      this.findUsername();
    });
  }

  loadUsernameAttemptCountsFromLocalStorage() {
    const storedCounts = localStorage.getItem('usernameAttemptCounts');
    if (storedCounts) {
      if (storedCounts == 'null')
        return {};
      return JSON.parse(storedCounts);
    } else
      return {};
  }

  saveUsernameAttemptCountsToLocalStorage(usernameAttemptCountsData) {
    localStorage.setItem('usernameAttemptCounts', JSON.stringify(usernameAttemptCountsData));
  }

  isAttemptCountExpired(attempt: { timestamp: number }) {
    const currentTime = Date.now();
    const expirationTime = attempt.timestamp + 30 * 60 * 1000; // 30 minutes in milliseconds
    return currentTime > expirationTime;
  }

  private getFailedLoginModalContentData(failedLoginModalName: 'ContractNotFound' | 'ContractCancelled' | 'AccountNotFound'): INotFoundModal {
    const emitError = {
      message: this.resourceService.getViewLabel(`Login_FailedLoginModal_Title_For_${failedLoginModalName}`),
      code: 'failedLoginModalName',
    }
    this.googleAnalyticsService.emitLoginEvent(GTMLoginEvents.LoginError, emitError);
    return {
      Title: this.resourceService.getViewLabel(`Login_FailedLoginModal_Title_For_${failedLoginModalName}`),
      Content: this.resourceService.transformContentData(this.resourceService.getViewLabel(`Login_FailedLoginModal_Body_Text_For_${failedLoginModalName}`)),
      ButtonLabel: this.resourceService.getViewLabel(`Login_FailedLoginModal_Button_Text_For_${failedLoginModalName}`),
      Id: failedLoginModalName,
    };
  }

  InitiateAccountNotFoundError() {
    if (this.loginService.failedLoginByUserGroup) {
      this.matDialog.closeAll();
      this.loginService.failedLoginByUserGroup = false;
      this.matDialog.open(FailedLoginModalComponent, {
        data: this.getFailedLoginModalContentData('AccountNotFound'),
      });
    }
  }

  async getToken() {
    return this.oktaAuth.token.getWithoutPrompt({
      scopes: this.configSettings.okta.scopes,
      issuer: this.configSettings.okta.issuer,
    }).then((res) => {
      return Promise.resolve({ isValid: true, token: res });
    }).catch(err => {
      return Promise.resolve({ isValid: false, token: null });
    });
  }

  isValidateToken(oktTokenStorage: string) {
    const tokens = JSON.parse(oktTokenStorage);
    const idToken = tokens.id_token ? tokens.id_token : tokens.idToken;
    const accessToken = tokens.access_token ? tokens.access_token : tokens.accessToken;
    const token = tokens.access_token && tokens.access_token.accessToken ? tokens.access_token.accessToken
      : (tokens.accessToken ? tokens.accessToken.accessToken : null);
    let isValid = false;
    if (idToken && accessToken && token) {
      isValid = true;
    }
    return isValid;
  }

  navigateToSignUp() {
    this.router.navigate([this.resourceService.getPortalSetting('URL_Create_Account').Value], { queryParamsHandling: 'preserve' });
  }

  getViewLabel(item: string) {
    const label = this.resourceService.getViewLabel(item) ?? '';
    return this.resourceService.transformContentData(label);
  }
}
